import type { DictationField, DictationMatch, DictationResult } from './dictation.js'
import type { ReportAnswer, ReportFieldType } from './report-checks.js'

/**
 * The cloud dictation pass: what we ask a model for, and what we accept back.
 *
 * `dictation.ts` is the matcher that runs on the phone. This is the other engine,
 * and the two deliberately return the same `DictationResult` — so the sheet, the
 * review flow and the tests do not care which one ran. That symmetry is the whole
 * design: the on-device matcher is not a degraded mode to be apologised for, it
 * is the fallback that works in a subfloor with no bars, and the cloud pass is an
 * upgrade to its comprehension rather than a replacement for its shape.
 *
 * Three rules hold here, and each one exists because the alternative was worse:
 *
 * 1. **The model returns strings and this file decides what they mean.** Asking
 *    for a typed union per field would need a schema branch per field type and
 *    would still need validating, because `strict` constrains the shape of JSON
 *    and not the sense of it. So the model's job is to say which question was
 *    addressed and quote the words; turning "seven point five" into 7.5 and
 *    rejecting "Mostly" for a Pass/Fail is arithmetic, and arithmetic belongs
 *    here where it is tested.
 * 2. **An answer that is not a legal answer is dropped, not coerced.** A chip
 *    list offering Tiled/Metal/Slate that comes back "Colorbond" is discarded.
 *    A wrong answer nobody typed is the failure mode that makes a technician stop
 *    reading the sheet, and the sheet already says plainly what it did not hear.
 * 3. **Nothing here writes an answer.** Same as every other assist: this produces
 *    suggestions that land in the review sheet ticked, and one tap rejects each.
 *    Decision 55.
 *
 * Dates stay out, as they do on device. "Last Tuesday" on a document somebody
 * signs is the one place a cheap guess is worse than a blank.
 */

/** The schema's name, sent with the request and echoed in logs. */
export const DICTATION_SCHEMA_NAME = 'report_answers'

/** One row of the model's reply, before it has been believed. */
type RawAnswer = { key: string; said: string; value: string }

/**
 * Field types that can be answered out loud.
 *
 * Photos and signatures are not words. Dates are words but are excluded on
 * purpose, per the note above. Shared with the sheet so the questions it lists as
 * askable are exactly the ones either engine will attempt — a sheet that lists a
 * question and then never hears it teaches people that dictation is broken.
 */
const SPEAKABLE: ReadonlySet<ReportFieldType> = new Set<ReportFieldType>([
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'DECIMAL',
  'BOOLEAN',
  'SELECT',
  'MULTI_SELECT',
  'PASS_FAIL',
  'RATING',
])

export function canBeSpoken(field: { type: ReportFieldType }): boolean {
  return SPEAKABLE.has(field.type)
}

/** The options a field offers, with the two-way fields given their words. */
function optionsOf(field: DictationField): string[] {
  if (field.options && field.options.length > 0) return field.options
  if (field.type === 'PASS_FAIL') return ['Pass', 'Fail']
  if (field.type === 'BOOLEAN') return ['Yes', 'No']
  return []
}

/**
 * The instruction, which is mostly a list of refusals.
 *
 * Written at the model rather than at a person, so it is blunt. The important
 * half is the licence to leave a question alone: a model asked to fill in a form
 * will fill in the form, and a plausible answer to a question the technician
 * never addressed is precisely the output this feature cannot ship with. The
 * on-device matcher gets this for free by being unable to invent anything.
 */
export const DICTATION_INSTRUCTIONS = [
  'You are sorting a field technician’s spoken account of a job onto the questions on one section of a report.',
  '',
  'Rules:',
  '- Only answer a question the words actually address. Leave every other question out of the array entirely.',
  '- Never infer, assume a default, or fill a gap with what is usually true of a property.',
  '- Quote the technician’s own words in `said`, as spoken, without tidying them.',
  '- For a question with a fixed list of options, `value` must be exactly one of those options, copied character for character.',
  '- For a question that takes several options, separate them with a comma.',
  '- For a yes/no question, `value` is exactly "Yes" or "No".',
  '- For a number, `value` is digits only, e.g. "7.5" — not "seven point five" and not "7.5%".',
  '- A negation is an answer: "no live termites" answers a live-termites question with "No".',
  '- If the words are ambiguous between two questions, leave both out.',
].join('\n')

/**
 * The JSON schema, built from the section's own fields.
 *
 * `key` is an enum of the real field keys, which is the one place `strict` earns
 * its keep: a model cannot answer a question this form does not ask, so the
 * mapping step below never has to guess what an invented key meant.
 */
export function dictationSchema(fields: DictationField[]): Record<string, unknown> {
  const keys = fields.filter(canBeSpoken).map((field) => field.key)

  return {
    type: 'object',
    additionalProperties: false,
    required: ['answers'],
    properties: {
      answers: {
        type: 'array',
        description: 'One entry per question the words actually addressed. Omit the rest.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['key', 'said', 'value'],
          properties: {
            key: { type: 'string', enum: keys },
            said: { type: 'string', description: 'The technician’s own words for this answer.' },
            value: { type: 'string', description: 'The answer, in the form the question takes.' },
          },
        },
      },
    },
  }
}

/**
 * The prompt body: the questions, then the words.
 *
 * The options are spelled out because they are the answer space — a model told
 * only that a field is a SELECT will invent a vocabulary. The hint line is the
 * same `dictationHint` the sheet shows a technician under each question, which
 * means the example a person is reading and the example the model is reading
 * cannot drift apart.
 */
export function dictationInput(transcript: string, fields: DictationField[]): string {
  const questions = fields.filter(canBeSpoken).map((field) => {
    const parts = [`- ${field.key} — ${field.label} [${field.type}]`]
    const options = optionsOf(field)

    if (options.length > 0) parts.push(`  options: ${options.join(' | ')}`)
    if (field.dictationHint) parts.push(`  example: ${field.dictationHint}`)

    return parts.join('\n')
  })

  return `Questions:\n${questions.join('\n')}\n\nTranscript:\n${transcript.trim()}`
}

function quote(text: string, limit = 72): string {
  const clean = text.trim().replace(/\s+/g, ' ')
  return clean.length <= limit ? clean : `${clean.slice(0, limit - 1).trimEnd()}…`
}

/** Compared without case, punctuation or the plural, as the matcher does. */
function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9.\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/s$/, '')
}

/** The option a spoken word is, or null when it is not one of them. */
function asOption(said: string, options: string[]): string | null {
  const wanted = normalise(said)
  if (wanted.length === 0) return null

  return (
    options.find((option) => normalise(option) === wanted) ??
    options.find((option) => {
      const candidate = normalise(option)
      // Contained rather than equal, so "tiled roof" answers a Tiled option and
      // "no live termites" does not have to be said as bare "No".
      return candidate.length > 2 && wanted.includes(candidate)
    }) ??
    null
  )
}

function asBoolean(said: string): boolean | null {
  const wanted = normalise(said)
  if (/^(ye|yes|y|true|pass|passed|present|accessible|ok)$/.test(wanted)) return true
  if (/^(no|n|false|fail|failed|absent|inaccessible|none)$/.test(wanted)) return false
  return null
}

function asNumber(said: string, whole: boolean): number | null {
  // Digits are pulled out of whatever the model wrapped them in, because "7.5%"
  // and "7.5 percent" both mean 7.5 and refusing them would be pedantry.
  const found = /-?\d+(\.\d+)?/.exec(said.replace(/,/g, ''))
  if (!found) return null

  const value = Number(found[0])
  if (!Number.isFinite(value)) return null
  if (whole && !Number.isInteger(value)) return Math.round(value)

  return value
}

/**
 * One model row turned into an answer of the field's own type, or null.
 *
 * Null is a rejection and rejections are silent to the technician: the field
 * simply appears in `unmatched`, which the sheet already reports as "got nothing
 * from that, and is left alone". That is the honest reading of a model answer we
 * could not make sense of.
 */
function coerce(field: DictationField, said: string): ReportAnswer | null {
  const value = said.trim()
  if (value.length === 0) return null

  switch (field.type) {
    case 'TEXT':
    case 'TEXTAREA':
      return value

    case 'NUMBER':
      return asNumber(value, true)

    case 'DECIMAL':
      return asNumber(value, false)

    case 'RATING': {
      const options = optionsOf(field)
      return options.length > 0 ? asOption(value, options) : asNumber(value, true)
    }

    case 'BOOLEAN':
      return asBoolean(value)

    case 'PASS_FAIL':
    case 'SELECT':
      return asOption(value, optionsOf(field))

    case 'MULTI_SELECT': {
      const options = optionsOf(field)
      const chosen = value
        .split(/,|;|\band\b/i)
        .map((part) => asOption(part, options))
        .filter((option): option is string => option !== null)

      const unique = [...new Set(chosen)]
      return unique.length > 0 ? unique : null
    }

    default:
      // Dates, photos, signatures and headings are never in the schema, so
      // reaching here means a key survived that should not have.
      return null
  }
}

function isRawAnswer(row: unknown): row is RawAnswer {
  if (typeof row !== 'object' || row === null) return false
  const candidate = row as Record<string, unknown>
  return (
    typeof candidate['key'] === 'string' &&
    typeof candidate['said'] === 'string' &&
    typeof candidate['value'] === 'string'
  )
}

/**
 * The model's reply, believed only as far as it checks out.
 *
 * Takes already-parsed JSON rather than a string, so the caller owns the parse
 * failure — a truncated body is a provider problem and gets a provider error,
 * while a well-formed reply full of answers we cannot use is this function's
 * problem and comes back as `unmatched`.
 *
 * A field is only ever answered once. A model that lists the same key twice has
 * contradicted itself, and the first row wins rather than the last, because the
 * first is the one whose quote the technician is most likely to recognise.
 */
export function readDictation(raw: unknown, fields: DictationField[]): DictationResult {
  const askable = fields.filter(canBeSpoken)
  const byKey = new Map(askable.map((field) => [field.key, field]))

  const rows =
    typeof raw === 'object' && raw !== null && Array.isArray((raw as { answers?: unknown }).answers)
      ? ((raw as { answers: unknown[] }).answers.filter(isRawAnswer) as RawAnswer[])
      : []

  const matches: DictationMatch[] = []
  const taken = new Set<string>()

  for (const row of rows) {
    const field = byKey.get(row.key)
    if (!field || taken.has(row.key)) continue

    const value = coerce(field, row.value)
    if (value === null) continue

    // The quote is the technician's words when the model gave them, and the bare
    // answer when it did not. Either way the sheet shows evidence rather than an
    // assertion, which is what makes a suggestion arguable.
    const heard = row.said.trim().length > 0 ? row.said : row.value

    taken.add(row.key)
    matches.push({
      fieldKey: field.key,
      value,
      source: 'heard',
      reason: `Heard “${quote(heard)}”.`,
      heard: quote(heard),
    })
  }

  return {
    matches,
    unmatched: askable.filter((field) => !taken.has(field.key)),
  }
}
